import { Fragment } from 'react'
import { Button, Text, UnstyledButton } from '@mantine/core'
import {
  IconArrowRight,
  IconCheck,
  IconCopy,
  IconMessageReport,
  IconSend,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import type { BriefingAppointment, BriefingItem } from '../../hooks/useBriefingDay'
import {
  doneKey,
  firstName,
  formatClock,
  formatWhen,
  kateStateText,
  kindSummary,
  languageName,
  splitFact,
  urgency,
  URGENCY_RANK,
  type Urgency,
} from './briefingCopy'
import { PatientAvatar, QuickButton } from './BriefingParts'

type BriefingDayProps = {
  appointments: BriefingAppointment[]
  emptyText: string
  // Visible items per appointment id.
  itemsByAppointment: Map<string, BriefingItem[]>
  // Epoch ms, ticking once a minute.
  now: number
  // The provider's local date ('YYYY-MM-DD').
  today: string
  selectedId: string | null
  onSelect: (appointmentId: string | null) => void
  // The check-in link: send it again by text/email, or copy it; the visit whose action is running.
  onResendLink: (appointment: BriefingAppointment) => void
  onCopyLink: (appointment: BriefingAppointment) => void
  linkBusyId: string | null
  // Items marked reviewed / done on this screen (doneKey): no longer counted as open.
  doneIds: Set<string>
}

type Tone = 'green' | 'blue' | 'amber' | 'gray'

const PILL_CLASS: Record<Tone, string> = {
  green: 'bg-[#effbf3] text-[#15803d]',
  blue: 'bg-blue-50 text-blue-700',
  amber: 'bg-amber-50 text-amber-800',
  gray: 'bg-gray-100 text-gray-600',
}

const NODE_CLASS: Record<Urgency | 'clear', string> = {
  urgent: 'border-[#e55535] bg-[#e55535]',
  soon: 'border-[#d97706] bg-[#d97706]',
  routine: 'border-gray-400 bg-white',
  clear: 'border-green-600 bg-white',
}

const QA_BUTTON = {
  variant: 'default',
  size: 'compact-xs',
  h: 28,
  px: 10,
  fz: 13,
  fw: 600,
  radius: 'md',
} as const

function scheduleHref(appointment: BriefingAppointment): string {
  if (!appointment.encounterId) return '/schedule'
  const params = new URLSearchParams({ encounter: appointment.encounterId })
  if (appointment.start) params.set('start', appointment.start)
  return `/schedule?${params.toString()}`
}

/** Where the visit itself stands, before and after its time. */
function visitStatus(
  appointment: BriefingAppointment,
  nowMs: number,
): { label: string; tone: Tone } | null {
  switch (appointment.readiness) {
    case 'completed':
      return { label: 'Visit done', tone: 'green' }
    case 'in-progress':
      return { label: 'In visit', tone: 'blue' }
    case 'ready':
      return { label: 'Checked in', tone: 'green' }
    default:
      break
  }
  const started = appointment.start !== null && Date.parse(appointment.start) <= nowMs
  if (appointment.appointmentStatus === 'pending') {
    return { label: 'Pending confirmation', tone: 'amber' }
  }
  if (started) return { label: 'Not started', tone: 'gray' }
  const kate = appointment.kate?.status
  if (kate === 'completed' || kate === 'reviewed') return { label: 'Check-in done', tone: 'green' }
  return appointment.notCheckedIn ? { label: 'Not checked in', tone: 'gray' } : null
}

function NowRow({ at }: { at: dayjs.Dayjs }) {
  return (
    <li className="grid grid-cols-[64px_16px_minmax(0,1fr)] items-center gap-x-2.5 py-[3px] text-[11.5px] font-bold text-red-500">
      <span className="text-right leading-tight">
        now
        <br />
        {at.format('h:mm A')}
      </span>
      <span
        aria-hidden
        className="relative z-[1] h-2.5 w-2.5 justify-self-center rounded-full bg-red-500"
      />
      <span aria-hidden className="h-0.5 rounded-sm bg-red-500/55" />
    </li>
  )
}

/**
 * The left column: today's visits as a timeline, each with its real status and a one-line summary
 * of what is open for that patient; the work itself is listed on the right. Selecting a visit
 * narrows the right column to its patient.
 */
export function BriefingDay({
  appointments,
  emptyText,
  itemsByAppointment,
  now: nowMs,
  today,
  selectedId,
  onSelect,
  onResendLink,
  onCopyLink,
  linkBusyId,
  doneIds,
}: BriefingDayProps) {
  if (appointments.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="md">
        {emptyText}
      </Text>
    )
  }

  const now = dayjs(nowMs)
  const nowAt = appointments.findIndex((a) => a.start !== null && dayjs(a.start).isAfter(now))

  return (
    <ol className="relative pt-0.5 before:absolute before:top-[18px] before:bottom-3.5 before:left-[81px] before:w-0.5 before:bg-gray-200 before:content-['']">
      {appointments.map((appointment, index) => {
        const selected = appointment.appointmentId === selectedId
        const open = (itemsByAppointment.get(appointment.appointmentId) ?? []).filter(
          (item) => !doneIds.has(doneKey(item)),
        )
        const top = open.reduce<Urgency | null>((best, item) => {
          const u = urgency(item, today)
          return best === null || URGENCY_RANK[u] < URGENCY_RANK[best] ? u : best
        }, null)
        const status = visitStatus(appointment, nowMs)
        const started = appointment.start !== null && Date.parse(appointment.start) <= nowMs
        const past = started && appointment.readiness !== 'in-progress'
        const kate = appointment.kate
        // Only while the check-in can still happen: after the start time the pill says it.
        const kateText =
          kate && appointment.notCheckedIn && !started
            ? kateStateText(kate.status, kate.sentAt, kate.sentVia)
            : null
        const name = appointment.patient?.name ?? null
        const meta = [
          appointment.visitReason,
          appointment.firstVisit === true
            ? 'First visit'
            : appointment.firstVisit === false
              ? 'Returning'
              : null,
        ]
          .filter(Boolean)
          .join(' · ')
        return (
          <Fragment key={appointment.appointmentId}>
            {index === nowAt && <NowRow at={now} />}
            <li className="grid grid-cols-[64px_16px_minmax(0,1fr)] items-start gap-x-2.5 py-1">
              <span className="pt-[11px] text-right text-[13px] font-semibold whitespace-nowrap text-gray-800 tabular-nums">
                {formatClock(appointment.start)}
              </span>
              <span
                aria-hidden
                className={`relative z-[1] mt-2.5 h-3.5 w-3.5 justify-self-center rounded-full border-[3px] ${
                  NODE_CLASS[top ?? 'clear']
                }`}
              />
              <div
                className={`flex min-w-0 flex-col gap-1.5 rounded-lg border border-gray-200 bg-white px-3 pt-2 pb-2.5 ${
                  selected ? 'shadow-[0_0_0_2px_#374151]' : 'hover:bg-gray-50'
                } ${past && !selected ? 'opacity-80' : ''}`}
              >
                <UnstyledButton
                  className="flex w-full items-start gap-2.5 text-left"
                  aria-expanded={selected}
                  onClick={() => onSelect(selected ? null : appointment.appointmentId)}
                >
                  <PatientAvatar name={name} photoUrl={appointment.patient?.photoUrl} size={32} />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span
                      className="truncate text-[15px] font-semibold text-gray-900"
                      title={name ?? undefined}
                    >
                      {name ?? 'Unknown patient'}
                    </span>
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      {meta && <span className="text-[13px] text-gray-600">{meta}</span>}
                      {status && (
                        <span
                          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${
                            PILL_CLASS[status.tone]
                          }`}
                        >
                          {status.tone === 'green' && <IconCheck size={12} stroke={2.5} />}
                          {status.label}
                        </span>
                      )}
                    </span>
                  </span>
                </UnstyledButton>
                <p className="text-[13.5px] leading-snug text-gray-800">
                  {open.length > 0 ? (
                    <>
                      <span className="font-semibold">
                        {open.length} {open.length === 1 ? 'item' : 'items'}{' '}
                        {started ? 'still open' : 'to review before the visit'}
                      </span>
                      <span className="text-gray-600"> · {kindSummary(open)}</span>
                    </>
                  ) : (
                    <span className="text-gray-600">Nothing open for this patient.</span>
                  )}
                </p>
                {kateText && !selected && (
                  <div className="grid grid-cols-[14px_minmax(0,1fr)] gap-2 text-[13.5px] leading-snug text-gray-800">
                    <span className="mt-[3px] text-gray-500">
                      <IconMessageReport size={14} />
                    </span>
                    <span>
                      <span className="font-semibold text-gray-600">Kate check-in · </span>
                      {kateText}
                    </span>
                  </div>
                )}
                {selected && (
                  <div className="mt-0.5 flex flex-col gap-2 border-t border-gray-100 pt-2">
                    {kate && kate.facts.length > 0 ? (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">
                          What Kate confirmed{firstName(name) ? ` with ${firstName(name)}` : ''}
                        </h4>
                        <p className="mb-2 text-[13px] text-gray-600">
                          {[
                            kate.completedAt ? `Checked in ${formatWhen(kate.completedAt)}` : null,
                            languageName(kate.language),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                        <ul className="grid grid-cols-1 gap-x-3.5 gap-y-2 sm:grid-cols-2">
                          {kate.facts.map((fact) => {
                            const { label, value } = splitFact(fact)
                            const info = label?.startsWith('Reason') ?? false
                            return (
                              <li
                                key={fact}
                                className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-[7px] text-[13.5px] leading-[1.35] text-gray-900"
                              >
                                {info ? (
                                  <span
                                    aria-hidden
                                    className="mt-[5px] ml-[4px] h-[6px] w-[6px] rounded-full bg-gray-400"
                                  />
                                ) : (
                                  <IconCheck size={15} className="mt-px text-green-600" />
                                )}
                                <span>
                                  {label && (
                                    <small className="block text-xs text-gray-600">{label}</small>
                                  )}
                                  {value}
                                </span>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    ) : (
                      kate &&
                      kateStateText(kate.status, kate.sentAt, kate.sentVia) && (
                        <div className="flex flex-col gap-2">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">Kate check-in</h4>
                            <p className="text-[13px] text-gray-700">
                              No answers yet for this visit.{' '}
                              {kateStateText(kate.status, kate.sentAt, kate.sentVia)}
                            </p>
                          </div>
                          {/* The server refuses a link for a visit that has already started. */}
                          {kate.status !== 'in_progress' && !started && (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <QuickButton
                                icon={<IconSend size={12} />}
                                loading={linkBusyId === `resend:${appointment.appointmentId}`}
                                disabled={linkBusyId !== null}
                                onClick={() => onResendLink(appointment)}
                              >
                                {kate.status === 'not_sent' ? 'Send link' : 'Resend link'}
                              </QuickButton>
                              <Button
                                {...QA_BUTTON}
                                leftSection={<IconCopy size={12} />}
                                loading={linkBusyId === `copy:${appointment.appointmentId}`}
                                disabled={linkBusyId !== null}
                                onClick={() => onCopyLink(appointment)}
                              >
                                Copy link
                              </Button>
                            </div>
                          )}
                        </div>
                      )
                    )}
                    <div className="flex flex-wrap items-center gap-1.5">
                      {appointment.encounterId && (
                        <Button
                          {...QA_BUTTON}
                          component={Link}
                          to={scheduleHref(appointment)}
                          rightSection={<IconArrowRight size={12} />}
                        >
                          {kate && kate.facts.length > 0
                            ? 'Open check-in card'
                            : 'Open appointment'}
                        </Button>
                      )}
                      {appointment.patient && (
                        <Button
                          {...QA_BUTTON}
                          component={Link}
                          to={`/patients/${appointment.patient.id}`}
                          rightSection={<IconArrowRight size={12} />}
                        >
                          Open patient chart
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </li>
          </Fragment>
        )
      })}
      {nowAt === -1 && <NowRow at={now} />}
    </ol>
  )
}
